import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type { OrgSettings } from "@backstory/protocol";
import { api, queryKeys } from "../../api";
import { authStore } from "../../auth/store";
import { OrgLayout } from "../../components/Shell";
import { PageHeader, Switch, useToast } from "../../components/ui";
import { ErrorState, Skeleton } from "../../components/States";
import { SaveBar } from "../../components/UnsavedChanges";
import { allowsFeature, useOrgEntitlements } from "../../features/billing/entitlements";

/** The four organization switches. Off means the feature never runs, whatever the plan says. */
type Flags = Pick<OrgSettings, "canvasDisabled" | "liveDisabled" | "liveNoRemoteControl" | "supportCallingDisabled">;

function flagsOf(s: OrgSettings): Required<Flags> {
  return {
    canvasDisabled: s.canvasDisabled ?? false,
    liveDisabled: s.liveDisabled ?? false,
    liveNoRemoteControl: s.liveNoRemoteControl ?? false,
    supportCallingDisabled: s.supportCallingDisabled ?? false,
  };
}

function same(a: Required<Flags>, b: Required<Flags>): boolean {
  return a.canvasDisabled === b.canvasDisabled && a.liveDisabled === b.liveDisabled && a.liveNoRemoteControl === b.liveNoRemoteControl && a.supportCallingDisabled === b.supportCallingDisabled;
}

export function FeaturesPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const org = useQuery({ queryKey: queryKeys.org(orgId), queryFn: () => api.orgs.get(orgId) });
  const ent = useOrgEntitlements(orgId);
  const canvasOn = allowsFeature(ent.data, "canvas");
  const liveOn = allowsFeature(ent.data, "live_assist");
  const [s, setS] = useState<Required<Flags> | null>(null);
  const [saved, setSaved] = useState<Required<Flags> | null>(null);
  useEffect(() => {
    if (org.data) {
      const f = flagsOf(org.data.settings);
      setS(f);
      setSaved(f);
    }
  }, [org.data]);
  const dirty = !!s && !!saved && !same(s, saved);

  const save = useMutation({
    mutationFn: () => api.orgs.patch(orgId, { settings: s ?? undefined }),
    onSuccess: () => {
      if (s) setSaved({ ...s });
      void qc.invalidateQueries({ queryKey: queryKeys.org(orgId) });
      void authStore.load();
      toast.push("success", "Features saved.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader title="Features" subtitle="Applies the next time the page loads." />
      {org.isPending || !s ? (
        <Skeleton />
      ) : org.isError ? (
        <ErrorState error={org.error} retry={() => void org.refetch()} />
      ) : (
        <>
          <div className="card form-card">
            <section className="section">
              <h2>Recording</h2>
              <div className="settings-stack">
                <Switch checked={canvasOn && !s.canvasDisabled} disabled={!canvasOn} onChange={(v) => setS({ ...s, canvasDisabled: !v })} label="Canvas recording" hint={canvasOn ? "Record canvas and WebGL on the page." : "Not included on this plan."} />
              </div>
            </section>
            <section className="section">
              <h2>Live Assist</h2>
              <div className="settings-stack">
                <Switch checked={liveOn && !s.liveDisabled} disabled={!liveOn} onChange={(v) => setS({ ...s, liveDisabled: !v })} label="Live Assist" hint={liveOn ? "Connect agents and show a help code." : "Not included on this plan."} />
                <Switch checked={liveOn && !s.liveNoRemoteControl} disabled={!liveOn || s.liveDisabled} onChange={(v) => setS({ ...s, liveNoRemoteControl: !v })} label="Remote control" hint="Let an agent control the page during Live Assist." />
                <Switch checked={liveOn && !s.supportCallingDisabled} disabled={!liveOn || s.liveDisabled} onChange={(v) => setS({ ...s, supportCallingDisabled: !v })} label="Support calls" hint="Let an agent start an audio call during Live Assist." />
              </div>
            </section>
          </div>
          <SaveBar
            dirty={dirty}
            saving={save.isPending}
            onSave={() => save.mutate()}
            onDiscard={() => {
              if (saved) setS(saved);
            }}
          />
        </>
      )}
    </OrgLayout>
  );
}
